import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSWRConfig } from 'swr';

import { useAuthSelector } from '@providers/AuthProvider/ui/AuthProvider';

import ChatContext from '@providers/ChatProvider/model/ChatContext';
import {
  getChatTokenUsageById,
  getMonthlyChatTokenUsage,
} from '@api/chat/apiService';
import useNotification from '@hooks/useNotification';
import { urls } from '@api/urls.ts';
import useToast from '@hooks/useToast';
import { ChatType, DocumentPromptPreviewStatus } from '@utils/enums.ts';
import { ChatMessage } from '@app/types/chat';
import { DocumentInstanceVersionChatContext } from '@shared/contexts/DocumentInstanceVersionChatContext.tsx';
import { getMyDocumentsCacheKeys } from '@shared/utils/my-documents-cache';
import {
  PageValues,
  usePageContext,
} from '@providers/PageMetaProvider/model/PageContext';
import {
  WebsocketContext,
  WebsocketContextValues,
} from '../model/WebsocketContext';
import {
  getStoredAccessToken,
  isAccessTokenExpiring,
  refreshAccessToken,
} from '@api/refreshAccessToken';
import { ReconnectingSocket } from '../model/reconnecting-socket';
import {
  applyDocumentChatChunk,
  applyDocumentChatReply,
} from '../model/document-chat-stream';

/**
 * @description Custom provider for Websocket.
 */

export enum WebsocketTypeEnum {
  DOCUMENT_STATUS = 'document_status',
  NOTIFICATION = 'notification',
  MESSAGE_RESPONSE = 'message_response',
  MESSAGE_CHUNK = 'message_chunk',
  DOCUMENT_GENERATOR_STATUS = 'document_generator_status',
  DOCUMENT_INSTANCE_STATUS = 'document_instance_status',
  DOCUMENT_PROMPT_PREVIEW_STATUS = 'document_prompt_preview_status',
  DEPOSITION_SUMMARY_TEMPLATE_STATUS = 'deposition_summary_template_status',
  DEPOSITION_SUMMARY_VERSION_STATUS = 'deposition_summary_version_status',
  USE_CASE_TEMPLATE_STATUS = 'use_case_template_status',
  USE_CASE_VERSION_STATUS = 'use_case_version_status',
  TASK_PROGRESS = 'task_progress',
  GENERATION_PROGRESS = 'generation_progress',
}

export const useWebSocket = () => useContext(WebsocketContext);

const WebsocketProvider: FC<PropsWithChildren> = ({ children }) => {
  const { mutate, cache } = useSWRConfig();
  const userData = useAuthSelector((state) => state.userData);
  const { getNotifications } = useNotification();
  const {
    setDraftMessage,
    setMessagesByChatId,
    setTokenUsage,
    setMonthlyTokenUsage,
    setIsLoading,
  } = useContext(ChatContext);
  const { setValues } = usePageContext();
  const documentVersionChatContext = useContext(
    DocumentInstanceVersionChatContext
  );

  const updateDocumentVersionMessages = useCallback(
    (updater: (prev: ChatMessage[]) => ChatMessage[] | ChatMessage[]) => {
      if (!documentVersionChatContext) {
        return;
      }

      const { setMessages } = documentVersionChatContext;
      setMessages((prev) => updater(prev));
    },
    [documentVersionChatContext]
  );

  const { showSuccess, showError } = useToast();

  const [message, setMessage] = useState<WebsocketContextValues | null>(null);
  // The connection lives outside React state; see reconnecting-socket.ts.
  const connectionRef = useRef<ReconnectingSocket | null>(null);

  // Helper to refresh all my-documents lists (with and without filters)
  // Improved to invalidate all cache keys matching the pattern
  const refreshMyDocuments = useCallback(() => {
    try {
      mutate(urls.myDocuments, undefined, { revalidate: true });
      getMyDocumentsCacheKeys(cache).forEach((key) => {
        mutate(key, undefined, { revalidate: true });
      });
    } catch (e) {
      console.error('Failed to refresh my documents', e);
    }
  }, [cache, mutate]);
  const receiveMessage = useCallback(
    (message: string) => {
      try {
        const messageDict = JSON.parse(message);
        const availableToken = localStorage.getItem('accessToken');
        if (availableToken === 'null') {
          connectionRef.current?.stop();
          return;
        }
        switch (messageDict.type) {
          case WebsocketTypeEnum.DOCUMENT_STATUS: {
            setMessage({
              type: messageDict.type,
              payload: messageDict?.message?.id,
            });

            if (messageDict?.message?.status === 'Completed') {
              showSuccess('Success', 'Demand file was generated');
            } else if (messageDict?.message?.status === 'Failed') {
              showError('Failed', 'Demand file generation failed');
            }

            break;
          }
          case WebsocketTypeEnum.MESSAGE_CHUNK: {
            const payload = messageDict.message || {};

            if (payload.chat_type === ChatType.documentVersion) {
              updateDocumentVersionMessages((prevMessages: ChatMessage[]) =>
                applyDocumentChatChunk(
                  prevMessages,
                  payload,
                  documentVersionChatContext?.activeChatId
                )
              );
            }

            break;
          }
          case WebsocketTypeEnum.DOCUMENT_GENERATOR_STATUS:
          case WebsocketTypeEnum.DEPOSITION_SUMMARY_TEMPLATE_STATUS:
          case WebsocketTypeEnum.USE_CASE_TEMPLATE_STATUS: {
            /**
             * description - trigger for document generator status updates
             */
            mutate(urls.allDocumentTemplates.base);
            const { status, message } = messageDict.message;

            if (status === 'Completed') {
              showSuccess(
                'Success',
                'Document generator template was generated successfully.'
              );
            } else if (status === 'Failed') {
              showError(
                'Failed',
                message ||
                  'There was an error generating your document template.'
              );
            }
            break;
          }
          case WebsocketTypeEnum.NOTIFICATION: {
            /**
             * description triggered for notification list updates
             */
            getNotifications();
            break;
          }
          case WebsocketTypeEnum.MESSAGE_RESPONSE: {
            const { chat_id, chat_type, message } = messageDict;
            const chatId = chat_id || message.chat_id;
            const chatType = chat_type || message.chat_type;

            if (chatType === ChatType.documentVersion) {
              updateDocumentVersionMessages((prevMessages: ChatMessage[]) =>
                applyDocumentChatReply(
                  prevMessages,
                  { ...message, chat_id: chatId },
                  documentVersionChatContext?.activeChatId
                )
              );
              break;
            }

            if (!chatId) {
              setDraftMessage(message);
            } else {
              setMessagesByChatId(chat_id!, (prevMessages) => {
                if (!prevMessages.find((msg) => msg.id === message.id)) {
                  return [...prevMessages, message];
                }
                return prevMessages;
              });
              setDraftMessage(message);

              // TODO: A chat is created after the first message is sent. This is a temporary solution.
              // Since the token usage is not updated immediately after the chat is created, we need to wait a bit.
              setTimeout(() => {
                getChatTokenUsageById(chatId, (data) => {
                  setTokenUsage(data);
                });
                if (message.chat_type) {
                  getMonthlyChatTokenUsage(message.chat_type, (data) => {
                    setMonthlyTokenUsage(data);
                  });
                }
              }, 1000);
            }

            setIsLoading(false);
            break;
          }
          case WebsocketTypeEnum.DOCUMENT_INSTANCE_STATUS: {
            setMessage({
              type: messageDict.type,
              payload: {
                ...messageDict.message,
                document_id:
                  messageDict.message?.document_id ?? messageDict.message?.id,
              },
            });
            const { status, message, document_name } = messageDict.message;
            const docName = document_name || 'document';
            // Refresh documents list when generation completes, fails, or is cancelled
            if (
              status === 'Completed' ||
              status === 'Failed' ||
              status === 'Draft'
            ) {
              refreshMyDocuments();
            }
            if (status === 'Failed') {
              showError(
                'Failed',
                message || `There was an error generating your ${docName}.`
              );
            } else if (status === 'Completed') {
              showSuccess(
                'Success',
                `Your ${docName} was generated successfully.`
              );
            }
            break;
          }
          case WebsocketTypeEnum.DEPOSITION_SUMMARY_VERSION_STATUS: {
            setMessage({
              type: messageDict.type,
              payload: {
                ...messageDict.message,
                document_id:
                  messageDict.message?.document_id ?? messageDict.message?.id,
              },
            });
            const { status, message, document_name } = messageDict.message;
            const docName = document_name || 'Deposition Summary';
            // Normalize status to handle case variations
            const normalizedStatus =
              typeof status === 'string' ? status.toLowerCase() : status;
            // Refresh documents list when generation completes
            if (
              normalizedStatus === 'completed' ||
              normalizedStatus === 'failed'
            ) {
              refreshMyDocuments();
              setValues?.((prev: Partial<PageValues>) => {
                if (
                  !prev.generateDepositionSummaryModalOpened ||
                  String(prev.generatingDocumentId || '') !==
                    String(
                      messageDict.message?.document_id ??
                        messageDict.message?.id ??
                        ''
                    )
                ) {
                  return prev;
                }
                return {
                  ...prev,
                  generateDepositionSummaryModalOpened: false,
                  generatingDocumentId: undefined,
                  generatingDocumentType: undefined,
                  progressModalAutoMinimize: true,
                  progressModalExpandedAt: undefined,
                };
              });
            }
            if (normalizedStatus === 'failed') {
              showError(
                'Failed',
                message || `There was an error generating your ${docName}.`
              );
            } else if (normalizedStatus === 'completed') {
              showSuccess(
                'Success',
                `Your ${docName} was generated successfully.`
              );
            }
            break;
          }
          case WebsocketTypeEnum.USE_CASE_VERSION_STATUS: {
            setMessage({
              type: messageDict.type,
              payload: {
                ...messageDict.message,
                document_id:
                  messageDict.message?.document_id ?? messageDict.message?.id,
              },
            });
            const {
              status,
              message,
              document_name,
              template_name,
              use_case_name,
            } = messageDict.message;
            const docName =
              document_name || template_name || use_case_name || 'report';
            const normalized =
              typeof status === 'string' ? status.toLowerCase() : status;
            // Refresh documents list when generation completes
            if (normalized === 'completed' || normalized === 'failed') {
              refreshMyDocuments();
              setValues?.((prev: Partial<PageValues>) => {
                if (
                  !prev.generateUseCaseModalOpened ||
                  String(prev.generatingDocumentId || '') !==
                    String(
                      messageDict.message?.document_id ??
                        messageDict.message?.id ??
                        ''
                    )
                ) {
                  return prev;
                }
                return {
                  ...prev,
                  generateUseCaseModalOpened: false,
                  generatingDocumentId: undefined,
                  generatingDocumentType: undefined,
                  progressModalAutoMinimize: true,
                  progressModalExpandedAt: undefined,
                };
              });
            }
            if (normalized === 'failed') {
              showError(
                'Failed',
                message || `There was an error generating your ${docName}.`
              );
            } else if (normalized === 'completed') {
              showSuccess(
                'Success',
                `Your ${docName} was generated successfully.`
              );
            }
            break;
          }
          case WebsocketTypeEnum.DOCUMENT_PROMPT_PREVIEW_STATUS: {
            mutate(urls.documentPromptPreview.base);
            const { status, message } = messageDict.message;
            if (status === DocumentPromptPreviewStatus.FAILED) {
              showError(
                'Failed',
                message || 'There was an error generating your document.'
              );
            } else if (status === DocumentPromptPreviewStatus.COMPLETED) {
              showSuccess(
                'Success',
                'Your document was generated successfully.'
              );
            }
            break;
          }
          case WebsocketTypeEnum.TASK_PROGRESS: {
            // Task progress event - will be handled by useGenerationProgress hook
            setMessage({
              type: messageDict.type,
              payload: messageDict.message,
            });
            break;
          }
          case WebsocketTypeEnum.GENERATION_PROGRESS: {
            // Overall generation progress event - will be handled by useGenerationProgress hook
            setMessage({
              type: messageDict.type,
              payload: messageDict.message,
            });
            break;
          }
          default: {
            // Fallback: if an unexpected report status type arrives, still refresh and notify
            try {
              const t = String(messageDict?.type || '');
              const payload = messageDict?.message;
              if (
                t.includes('use_case') &&
                payload &&
                typeof payload === 'object'
              ) {
                const normalized =
                  typeof payload.status === 'string'
                    ? payload.status.toLowerCase()
                    : payload.status;
                // Refresh documents list when generation completes
                if (normalized === 'completed' || normalized === 'failed') {
                  refreshMyDocuments();
                }
                if (normalized === 'failed') {
                  showError(
                    'Failed',
                    payload.message ||
                      'There was an error generating your document.'
                  );
                } else if (normalized === 'completed') {
                  showSuccess(
                    'Success',
                    'Your document was generated successfully.'
                  );
                }
              }
            } catch (e) {
              // no-op
            }
            break;
          }
        }
      } catch (e) {
        console.log(e);
        console.log('receiveMessage error');
      }
    },
    [
      showSuccess,
      showError,
      setMessage,
      setDraftMessage,
      setMessagesByChatId,
      setTokenUsage,
      setMonthlyTokenUsage,
      setIsLoading,
      updateDocumentVersionMessages,
      documentVersionChatContext,
      getNotifications,
      mutate,
      refreshMyDocuments,
    ]
  );

  // Always hand messages to the latest handler without rebuilding the socket.
  const receiveMessageRef = useRef(receiveMessage);
  receiveMessageRef.current = receiveMessage;

  useEffect(() => {
    const connection = new ReconnectingSocket({
      getUrl: () => {
        const token = localStorage.getItem('accessToken');
        if (!token || token === 'null') return null;
        const rawWsBase =
          import.meta.env.VITE_APP_WS_URL ||
          import.meta.env.VITE_APP_API_URL ||
          '';
        const wsBase = rawWsBase.replace(/^http/i, 'ws').replace(/\/$/, '');
        return `${wsBase}/ws/?token=${token.replace(/"/g, '')}`;
      },
      // The URL carries the access token and only the HTTP layer used to
      // refresh it, so a socket opened after the token expired was accepted
      // and closed straight away (ZLAYT-1447). Refresh first when it is about
      // to expire, or when the server just closed with 4401.
      ensureFreshToken: async (force) => {
        const token = getStoredAccessToken();
        if (!token) return;
        if (force || isAccessTokenExpiring(token)) await refreshAccessToken();
      },
      onMessage: (data) => {
        try {
          receiveMessageRef.current(data);
        } catch (e) {
          console.error('[WebSocket] onmessage error', e);
        }
      },
      // Connection chatter only in development; production consoles stay quiet.
      log: import.meta.env.DEV ? console.debug : undefined,
    });
    connectionRef.current = connection;
    return () => {
      connection.stop();
      connectionRef.current = null;
    };
  }, []);

  // Connect once signed in, disconnect on sign-out.
  useEffect(() => {
    if (userData) connectionRef.current?.start();
    else connectionRef.current?.stop();
  }, [userData]);

  // Periodic check: reconnect if the socket dropped and no retry is pending.
  useEffect(() => {
    const id = setInterval(
      () => connectionRef.current?.ensureConnected(),
      30000
    );
    return () => clearInterval(id);
  }, []);

  return (
    <WebsocketContext.Provider value={message}>
      {children}
    </WebsocketContext.Provider>
  );
};

export default WebsocketProvider;
